import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, AlertTriangle, ArrowRight } from 'lucide-react';

const outcomes = {
  ack: {
    icon: CheckCircle2,
    tone: 'emerald',
    title: 'Duty Acknowledged',
    subtitle: 'Thank you for acknowledging. The duty has been confirmed on your schedule.',
  },
  decline_reassigned: {
    icon: AlertTriangle,
    tone: 'amber',
    title: 'Thanks — Looking for a Replacement',
    subtitle: 'A new teacher has been auto-assigned to cover this period.',
  },
  decline_exhausted: {
    icon: XCircle,
    tone: 'rose',
    title: 'Admin Has Been Notified',
    subtitle: 'We\'ve reached the auto-reassignment limit. An admin will arrange cover manually.',
  },
  decline_no_replacement: {
    icon: XCircle,
    tone: 'rose',
    title: 'No Replacement Found',
    subtitle: 'No other eligible teacher is free right now — the admin has been alerted.',
  },
  invigilation_declined: {
    icon: AlertTriangle,
    tone: 'amber',
    title: 'Thanks — Admin Will Reassign',
    subtitle: 'Your decline has been recorded. An admin will arrange a replacement invigilator.',
  },
  already_acked: {
    icon: CheckCircle2,
    tone: 'emerald',
    title: 'Already Acknowledged',
    subtitle: 'This duty was previously confirmed — no further action needed.',
  },
  forbidden: {
    icon: XCircle,
    tone: 'rose',
    title: 'Link Not Valid for You',
    subtitle: 'This response link belongs to a different teacher\'s account.',
  },
  invalid: {
    icon: XCircle,
    tone: 'rose',
    title: 'Assignment Not Found',
    subtitle: 'The duty referenced by this link no longer exists.',
  },
  error: {
    icon: XCircle,
    tone: 'rose',
    title: 'Something Went Wrong',
    subtitle: 'We couldn\'t process your response. Please open Schoolzest and try again.',
  },
};

const AssignmentResponsePage = () => {
  const [sp] = useSearchParams();
  const outcome = sp.get('outcome') || 'error';
  const info = outcomes[outcome] || outcomes.error;
  const Icon = info.icon;

  const teacher = sp.get('teacher');
  const date = sp.get('date');
  const period = sp.get('period');
  const klass = sp.get('klass');
  const subject = sp.get('subject');
  const newTeacher = sp.get('new_teacher');
  const attemptsLeft = sp.get('attempts_left');
  const attempts = sp.get('attempts');
  const reason = sp.get('reason');

  const toneBg = {
    emerald: 'bg-emerald-50 border-emerald-200',
    amber: 'bg-amber-50 border-amber-200',
    rose: 'bg-rose-50 border-rose-200',
  }[info.tone];
  const toneText = {
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    rose: 'text-rose-700',
  }[info.tone];

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50" data-testid="assignment-response-page">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        <div className={`px-6 py-8 ${toneBg} border-b`}>
          <div className="flex items-center gap-3">
            <Icon className={`h-10 w-10 ${toneText}`} strokeWidth={1.5} />
            <div>
              <h1 className={`text-xl font-semibold ${toneText}`}>{info.title}</h1>
              <p className="text-sm text-slate-600 mt-1">{info.subtitle}</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-3 text-sm">
          {reason && <p className="text-rose-600 text-sm italic">{reason}</p>}
          {teacher && <Row label="Teacher" value={teacher} />}
          {date && <Row label="Date" value={date} />}
          {period && <Row label="Period" value={period} />}
          {klass && <Row label="Class" value={klass} />}
          {subject && <Row label="Subject" value={subject} />}
          {newTeacher && <Row label="Reassigned to" value={newTeacher} highlight />}
          {attemptsLeft != null && (
            <p className="text-xs text-slate-500 italic">
              {attemptsLeft} reassignment{attemptsLeft === '1' ? '' : 's'} remaining before admin intervention.
            </p>
          )}
          {attempts != null && (
            <p className="text-xs text-rose-500 italic">Reassignment cap: {attempts}.</p>
          )}
          <a
            href="https://schoolzest.com/login"
            className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-blue-700 hover:text-blue-900"
            data-testid="return-to-app-link"
          >
            Open Schoolzest <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, highlight }) => (
  <div className="flex items-start gap-3">
    <span className="w-28 shrink-0 text-xs uppercase tracking-wider text-slate-500 mt-0.5">{label}</span>
    <span className={`font-medium ${highlight ? 'text-emerald-700' : 'text-slate-900'}`}>{value}</span>
  </div>
);

export default AssignmentResponsePage;
